import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';

import { FilenameConvention } from 'src/app/models/filename-conventions/filename-convention';
import { TokenFormat } from 'src/app/models/filename-conventions/token-format';

import { classifyChip, chipLabelOf } from '../shared/chip-classifier';
import {
  ConventionSummary,
  conventionModeIcon,
  conventionModeLabel,
  conventionSummary,
  evEntriesLabel,
  evMasterSuiteLabel,
  firstRangeEntryCasingLabel,
  rangeEntriesLabel,
} from '../shared/convention-labels';

export type FilenameConventionsSubTab = 'range' | 'ev';

@Component({
  selector: 'app-filename-conventions-panel',
  templateUrl: './filename-conventions-panel.component.html',
  styleUrls: ['./filename-conventions-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FilenameConventionsPanelComponent {
  @Input() filenameConventions: FilenameConvention[] = [];
  @Input() loaded = false;
  @Input() canWrite = false;
  @Input() canAudit = false;

  @Output() createRange = new EventEmitter<void>();
  @Output() createEv = new EventEmitter<void>();
  @Output() updateConvention = new EventEmitter<FilenameConvention>();
  @Output() deleteConvention = new EventEmitter<FilenameConvention>();
  @Output() viewConventionHistory = new EventEmitter<FilenameConvention>();

  activeSubTab: FilenameConventionsSubTab = 'range';

  readonly classifyChip = classifyChip;
  readonly chipLabelOf = chipLabelOf;

  constructor(private readonly cdr: ChangeDetectorRef) {}

  get rangeConventions(): FilenameConvention[] {
    return this.filenameConventions.filter((row) => row.value.payload.mode === 'rangeBased');
  }

  get evConventions(): FilenameConvention[] {
    return this.filenameConventions.filter((row) => row.value.payload.mode === 'evBased');
  }

  get visibleConventions(): FilenameConvention[] {
    return this.activeSubTab === 'range' ? this.rangeConventions : this.evConventions;
  }

  metaIcon(row: FilenameConvention): string {
    return conventionModeIcon(row);
  }

  metaLabel(row: FilenameConvention): string {
    const summary = conventionSummary(row);
    const mode = conventionModeLabel(row);
    if (row.value.payload.mode === 'rangeBased') {
      return `${mode} · ${rangeEntriesLabel(summary.entryCount)}`;
    }
    return `${mode} · ${evEntriesLabel(summary.entryCount)}`;
  }

  summary(row: FilenameConvention): ConventionSummary {
    return conventionSummary(row);
  }

  masterSuiteLabel(count: number): string {
    return evMasterSuiteLabel(count);
  }

  firstEntryCasing(row: FilenameConvention): string {
    return firstRangeEntryCasingLabel(row);
  }

  rangePreview(row: FilenameConvention): readonly TokenFormat[] {
    const payload = row.value.payload;
    if (payload.mode !== 'rangeBased') return [];
    const first = payload.entries[0];
    if (!first || first.tokenFormats.length === 0) return [];
    return [...first.tokenFormats].sort((a, b) => a.order - b.order);
  }

  trackTokenFormat(_index: number, chip: TokenFormat): string {
    return `${chip.order}:${chip.value}`;
  }

  onSubTabChanged(subTab: FilenameConventionsSubTab): void {
    this.activeSubTab = subTab;
    this.cdr.markForCheck();
  }

  onCreateRange(): void {
    this.createRange.emit();
  }

  onCreateEv(): void {
    this.createEv.emit();
  }

  onUpdateConvention(row: FilenameConvention): void {
    this.updateConvention.emit(row);
  }

  onDeleteConvention(row: FilenameConvention): void {
    this.deleteConvention.emit(row);
  }

  onViewConventionHistory(row: FilenameConvention): void {
    this.viewConventionHistory.emit(row);
  }
}
